// Users › Edit › "Reset two-factor" (2026-10-20). Two stages in one small dialog:
//   confirm  — what the reset does (their authenticator and backup codes stop working; they set it
//              up again at their next sign-in)
//   code     — shown only when the BE answers two_factor_admin_code_required: the acting Admin
//              confirms with the current code from THEIR OWN authenticator app (step-up)
// Portaled to body so it sits above the Edit User modal that opens it.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldOff, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { displayUserType } from "../../types/User";

export type TwoFactorResetOutcome =
    | { ok: true }
    | { ok: false; status: number; code?: string; message?: string; retryAfterSeconds?: number };

interface Props {
    user: displayUserType;
    onClose: () => void;
    resetTwoFactor: (user: displayUserType, adminOtp?: string) => Promise<TwoFactorResetOutcome>;
}

const DIGITS = /^\d{6}$/;

const TwoFactorResetDialog = ({ user, onClose, resetTwoFactor }: Props) => {
    const { t } = useTranslation();
    const [stage, setStage] = useState<"confirm" | "code">("confirm");
    const [otp, setOtp] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [busy, onClose]);

    const run = async (code?: string) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const out = await resetTwoFactor(user, code);
            if (out.ok) { onClose(); return; }
            if (out.code === "two_factor_admin_code_required") {
                setStage("code");
                window.setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }
            if (out.code === "two_factor_admin_code_invalid") {
                setError(t("users_reset_two_factor_stepup_wrong"));
                setOtp("");
                window.setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }
            if (out.code === "two_factor_self_reset_refused") { setError(t("users_reset_two_factor_self_refused")); return; }
            if (out.status === 423) {
                const minutes = Math.max(1, Math.ceil((typeof out.retryAfterSeconds === "number" ? out.retryAfterSeconds : 900) / 60));
                setError(t("users_reset_two_factor_stepup_locked", { minutes: String(minutes) }));
                return;
            }
            setError(out.message ?? t("users_reset_two_factor_error"));
        } finally {
            setBusy(false);
        }
    };

    const dialog = (
        <div className="modal-overlay" onClick={() => { if (!busy) onClose(); }} role="dialog" aria-modal="true" aria-labelledby="tfa-reset-title">
            <div className="modal-card tfa-reset-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2 id="tfa-reset-title">
                        {stage === "confirm" ? t("users_reset_two_factor_title") : t("users_reset_two_factor_stepup_title")}
                    </h2>
                    <button type="button" className="modal-close" onClick={onClose} aria-label={t("close")} disabled={busy}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    {stage === "confirm" ? (
                        <>
                            <p className="tfa-reset-body">
                                <ShieldOff size={18} aria-hidden="true" className="tfa-reset-icon" />
                                <span>{t("users_reset_two_factor_body", { name: user.name })}</span>
                            </p>
                            {error && <p className="user-edit-field-hint user-edit-field-hint--error" role="alert">{error}</p>}
                            <div className="user-edit-reset-actions">
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => run()} disabled={busy}>
                                    {busy ? t("saving") : t("users_reset_two_factor_confirm")}
                                </button>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t("cancel")}</button>
                            </div>
                        </>
                    ) : (
                        <form className="modal-form" onSubmit={(e) => { e.preventDefault(); if (DIGITS.test(otp)) void run(otp); }}>
                            <p className="tfa-reset-body"><span>{t("users_reset_two_factor_stepup_body")}</span></p>
                            <label>
                                <span className="modal-form-label">{t("users_reset_two_factor_stepup_label")}</span>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    name="adminOtp"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    autoComplete="one-time-code"
                                    aria-label={t("users_reset_two_factor_stepup_label")}
                                    maxLength={6}
                                    placeholder="000000"
                                    value={otp}
                                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
                                    disabled={busy}
                                    autoFocus
                                    aria-invalid={error ? true : undefined}
                                />
                                {error
                                    ? <span className="user-edit-field-hint user-edit-field-hint--error" role="alert">{error}</span>
                                    : <span className="user-edit-field-hint">{t("users_reset_two_factor_stepup_hint")}</span>}
                            </label>
                            <div className="user-edit-reset-actions">
                                <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !DIGITS.test(otp)}>
                                    {busy ? t("saving") : t("users_reset_two_factor_stepup_confirm")}
                                </button>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t("cancel")}</button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(dialog, document.body);
};

export default TwoFactorResetDialog;
